import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import { useTranslation } from 'react-i18next'

/**
 * What FaceWise shows before it has enough responses to average: a count, not
 * a result. Under the floor the backend deliberately sends an empty curve
 * (`below_min_respondents`), because a mean over two people describes those two
 * people and nothing else — drawing it would look like a finding.
 *
 * So this is not an error and not an empty state: it says how many responses
 * came in, how many are still missing, and why the chart is waiting. Pro has no
 * floor and never renders it.
 *
 * Two different situations end up here and must not be told the same story.
 * The study may simply still be collecting — then the count is the study's own
 * and a progress bar means something. Or the researcher may have filtered down
 * to a group that is too small, on a study with plenty of responses — then the
 * count describes the selected group, "two more to go" would be meaningless,
 * and the useful thing to offer is the way back to everybody.
 *
 * Copy lives in the `monitoring` namespace — the study screen's own — rather
 * than in `emotions`, which holds the vocabulary of the chart itself.
 */
export default function MinRespondentsNotice({
  count,
  min,
  filtered = false,
  onClearFilters,
}: {
  count: number
  min: number
  /** The count describes a filtered group, not the whole study. */
  filtered?: boolean
  onClearFilters?: () => void
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('monitoring')
  const remaining = Math.max(0, min - count)
  const progress = min > 0 ? Math.min(100, (count / min) * 100) : 0

  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, borderColor: 'divider' }}>
      <Stack spacing={1.75} alignItems="center" sx={{ maxWidth: 460, mx: 'auto', textAlign: 'center' }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '14px',
            bgcolor: c.coralSoft,
            color: c.coral,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <GroupsRoundedIcon sx={{ fontSize: 24 }} />
        </Box>

        <Box>
          <Typography
            sx={{
              fontFamily: theme.affexy.fonts.heading,
              fontWeight: 700,
              fontSize: 34,
              lineHeight: 1.1,
              color: c.ink,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {/* `n`, not `count` — this is a ratio, not a pluralised noun. */}
            {t('minRespondents.counter', { n: count, min })}
          </Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: c.ink48, mt: 0.5 }}>
            {t(filtered ? 'minRespondents.inGroup' : 'minRespondents.collected')}
          </Typography>
        </Box>

        {/* A progress bar only reads as progress when the study is filling up.
            For a filtered group it would suggest the group grows if you wait. */}
        {!filtered && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              width: '100%',
              height: 8,
              borderRadius: 4,
              bgcolor: c.gray100,
              '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: c.coral },
            }}
          />
        )}

        <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 18, color: c.ink }}>
          {t(filtered ? 'minRespondents.filteredTitle' : 'minRespondents.title')}
        </Typography>
        <Typography sx={{ fontSize: 14, color: c.ink56, lineHeight: 1.55 }}>
          {t(filtered ? 'minRespondents.filteredBody' : 'minRespondents.body', { min })}
        </Typography>
        {!filtered && remaining > 0 && (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: c.ink72 }}>
            {t('minRespondents.remaining', { count: remaining })}
          </Typography>
        )}
        {filtered && onClearFilters && (
          <Button variant="outlined" size="small" onClick={onClearFilters}>
            {t('minRespondents.clearFilters')}
          </Button>
        )}
      </Stack>
    </Paper>
  )
}
